import { useMemo, useState } from "react";
import { InfiniteData, useQueryClient } from "react-query";

import { DateTime } from "@bps/date-time";
import { Stack, Text, useTheme } from "@bps/fluent-ui";
import {
  FieldDevice,
  FieldDeviceCommandResponse,
  FieldDeviceCommandResponseArgs
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useCommandResponses
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { PagingResponse } from "@libs/paging/paging-response.type";
import { getPagesResult } from "@libs/paging/paging.utils";

import {
  CommandAuditFilterFormValues,
  CommandAuditFilters,
  initialCommandAuditFilterValues
} from "./CommandAuditFilters";
import { CommandAuditTable } from "./CommandAuditTable";

const normalizeText = (value: string): string | undefined => {
  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

const toUtcIsoBoundary = ({
  date,
  isEndOfDay
}: {
  date?: Date;
  isEndOfDay?: boolean;
}): string | undefined => {
  if (!date) {
    return undefined;
  }

  const dateTime = DateTime.fromJSDate(date);
  const normalizedDateTime =
    isEndOfDay === true ? dateTime.endOf("day") : dateTime.startOf("day");

  return normalizedDateTime.toISO() ?? undefined;
};

interface CommandAuditSectionProps {
  device?: FieldDevice;
}

export const CommandAuditSection: React.FC<CommandAuditSectionProps> = ({
  device
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [filterValues, setFilterValues] = useState<
    CommandAuditFilterFormValues
  >(initialCommandAuditFilterValues(device?.id ?? ""));

  const [searchArgs, setSearchArgs] = useState<
    FieldDeviceCommandResponseArgs | undefined
  >();

  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    refetch,
    isLoading,
    isRefetching,
    isFetchingNextPage,
    error
  } = useCommandResponses(searchArgs);

  const items = useMemo<FieldDeviceCommandResponse[]>(() => {
    return getPagesResult<FieldDeviceCommandResponse>(data);
  }, [data]);

  const totalRecords = data?.pages[0]?.total ?? 0;

  const handleScrolledToBottom = async (): Promise<void> => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const handleSearch = (): void => {
    setHasSearched(true);
    const nextArgs: FieldDeviceCommandResponseArgs = {
      fieldDeviceId: normalizeText(filterValues.fieldDeviceId),
      correlationId: normalizeText(filterValues.correlationId),
      softwarePackageCode: normalizeText(filterValues.softwarePackageCode),
      commandName: normalizeText(filterValues.commandName),
      responseCode: filterValues.responseCode,
      sourceType: normalizeText(filterValues.sourceType ?? ""),
      startDateTime: toUtcIsoBoundary({ date: filterValues.startDateTime }),
      endDateTime: toUtcIsoBoundary({
        date: filterValues.endDateTime,
        isEndOfDay: true
      })
    };
    setSearchArgs(nextArgs);
  };

  const handleRefresh = async (): Promise<void> => {
    if (!searchArgs) {
      return;
    }

    queryClient.setQueryData<
      InfiniteData<PagingResponse<FieldDeviceCommandResponse>> | undefined
    >([FieldOpsCacheKeys.CommandResponses, searchArgs], previousData => {
      if (!previousData || previousData.pages.length <= 1) {
        return previousData;
      }

      return {
        ...previousData,
        pages: previousData.pages.slice(0, 1),
        pageParams: previousData.pageParams.slice(0, 1)
      };
    });

    await refetch({
      refetchPage: (_, index): boolean => index === 0
    });
  };

  let resultsContent: React.ReactNode;

  if (!hasSearched) {
    resultsContent = (
      <Stack horizontalAlign="center" verticalAlign="center" grow>
        <Text variant="small">Select filters and click Search.</Text>
      </Stack>
    );
  } else if (items.length === 0 && !isLoading && !isRefetching) {
    resultsContent = (
      <Stack horizontalAlign="center" verticalAlign="center" grow>
        <Text variant="small">
          Your search returned no results, please refine your search term(s).
        </Text>
      </Stack>
    );
  } else {
    resultsContent = (
      <CommandAuditTable
        items={items}
        error={error ?? undefined}
        isLoading={(isLoading || isRefetching) && items.length === 0}
        hasNextPage={hasNextPage}
        handleScrolledToBottom={handleScrolledToBottom}
      />
    );
  }

  return (
    <Stack
      verticalFill
      styles={{
        root: {
          padding: theme.spacing.s1,
          width: "100%",
          overflow: "hidden"
        }
      }}
      tokens={{ childrenGap: theme.spacing.m }}
    >
      <Stack
        tokens={{ childrenGap: theme.spacing.s1 }}
        styles={{
          root: {
            border: `1px solid ${theme.semanticColors.variantBorder}`,
            padding: theme.spacing.m
          }
        }}
      >
        <CommandAuditFilters
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          onSearch={handleSearch}
          onRefresh={(): void => {
            void handleRefresh();
          }}
          canRefresh={hasSearched}
          isRefreshing={isRefetching && !isFetchingNextPage}
          displayFieldDeviceIdFilter={device === undefined}
        />
      </Stack>

      <Stack horizontalAlign="end">
        <Text>{totalRecords} records found</Text>
      </Stack>

      {resultsContent}
    </Stack>
  );
};
